import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { useQueryClient } from '@tanstack/react-query'
import { useCurrentNetwork } from '@mysten/dapp-kit-react'
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs'
import { NewPosition } from './NewPosition'
import { PositionOverview } from './PositionOverview'
import { MyPositions } from 'modules/MyPositions/MyPositions'
import { Button } from 'components/Button/Button'
import { IconPlus } from 'components/Icons/IconPlus'
import { PAIR_INFOS, PairInfo } from 'utils/pair-info'
import { LpVaultsMenu } from './LpVaultsMenu'
import { NoAvailablePosition } from './NoAvailablePosition'
import { LpVaultExplanation } from './LpVaultExplanation'
import { useIsMobile } from 'utils/useIsMobile'
import { HEADER_ID } from 'components/Header/Header'
import { LpActivityHistory } from 'modules/ActivityHistory/LpActivityHistory'
import { MyPositionsContentLoader } from 'modules/MyPositions/ContentLoaders'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useUserPositions } from '@/shared/kai/hooks/useUserPositions'
import { useUserPositionDetails } from '@/shared/kai/hooks/useUserPositionDetails'
import { Tabs } from 'components/Tabs/Tabs'
import { Analytics } from './Analytics/Analytics'
import { positionMetricsQueryKey } from './Analytics/hooks/usePositionMetrics'
import { positionMetricsHistoryQueryKey } from './Analytics/hooks/usePositionMetricsHistory'
import { NewPositionQueryParams } from 'modules/Leaderboard/const'
import usePrevious from 'utils/usePrevious'
import { usePostHogEntityViewed } from 'utils/usePostHogEntityViewed'
import { usePostHogEntityAction } from 'utils/usePostHogEntityAction'
import { useCurrentWalletAddress } from 'utils/useCurrentWalletAddress'
import { useGlobalBanner } from 'utils/useGlobalBanner'
import { GLOBAL_BANNER_HEIGHT } from 'components/GlobalBanner/GlobalBanner'

const Page = styled.div<{ $hasBanner: boolean }>`
  min-height: calc(100vh - 462px);
  margin-top: calc(124px + ${props => (props.$hasBanner ? GLOBAL_BANNER_HEIGHT : 0)}px);

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    padding: 0 20px;
    margin-top: calc(100px + ${props => (props.$hasBanner ? GLOBAL_BANNER_HEIGHT : 0)}px);
  }

  @media screen and (min-width: ${p => p.theme.breakpoints.md}px) {
    max-width: 1200px;
    margin-left: auto;
    margin-right: auto;
  }
`

const NewPositionButton = styled(Button)`
  display: flex;
  align-items: center;

  @media screen and (min-width: ${p => p.theme.breakpoints.md}px) {
    min-width: 176px;
  }

  & > svg {
    margin-left: 8px;
  }
`

interface Props {
  pairInfo: PairInfo<string>
}

const smoothScrollToRef = (ref: React.RefObject<HTMLElement | null>, condition: boolean, hasBanner: boolean) => {
  if (condition && ref.current) {
    const elementRect = ref.current.getBoundingClientRect()
    const scrollY = window.scrollY

    const headerElement = document.getElementById(HEADER_ID)
    const fixedHeaderHeight = headerElement ? headerElement.offsetHeight : 0
    const fixedBannerHeight = hasBanner ? GLOBAL_BANNER_HEIGHT : 0
    const fixedHeight = fixedHeaderHeight + fixedBannerHeight

    window.scrollTo({
      top: scrollY + elementRect.top - fixedHeight,
      behavior: 'smooth',
    })
  }
}

export const DONT_SHOW_THIS_AGAIN_LP_LS_KEY = 'lp-vault-not-show'

export const LpVaultDetails = ({ pairInfo }: Props) => {
  usePostHogEntityViewed('lp:pair_details_view', {
    entityId: `${pairInfo.coinX.displaySymbol}-${pairInfo.coinY.displaySymbol}`,
  })

  const { isEnabled: hasBanner } = useGlobalBanner()

  const { capture } = usePostHogEntityAction()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()
  const queryStringPositionId = searchParams.get('position')
  const initialConfigId = searchParams.get(NewPositionQueryParams.configId)
  const initialMinPrice = searchParams.get(NewPositionQueryParams.minPrice)
  const initialMaxPrice = searchParams.get(NewPositionQueryParams.maxPrice)

  const isCopiedPosition = Boolean(initialConfigId && initialMinPrice && initialMaxPrice)

  const didScrollRef = useRef(false)

  const clickedRef = useRef(false)

  const walletAddress = useCurrentWalletAddress()
  const isConnected = !!walletAddress

  const newPositionRef = useRef<HTMLTableElement>(null)
  const positionOverviewRef = useRef<HTMLTableElement>(null)

  const isMobile = useIsMobile()

  const {
    positionInfos: allPositionInfos,
    refetch: refetchUserPositionInfos,
    isLoading: isLoadingUserPositionInfos,
  } = useUserPositions(walletAddress)

  const isLoading = isLoadingUserPositionInfos

  const prevIsLoading = usePrevious(isLoading)

  const positionInfos = useMemo(() => {
    return allPositionInfos.filter(p => {
      const configInfo = p.lastPosition.configInfo
      return (
        (configInfo.X.typeName === pairInfo.coinX.typeName && configInfo.Y.typeName === pairInfo.coinY.typeName) ||
        (configInfo.X.typeName === pairInfo.coinY.typeName && configInfo.Y.typeName === pairInfo.coinX.typeName)
      )
    })
  }, [allPositionInfos, pairInfo])

  const [activePositionId, setActivePositionId] = useState<string>()
  const queryClient = useQueryClient()
  const network = useCurrentNetwork()

  const activePosition = useMemo(
    () => positionInfos.find(p => p.positionId === activePositionId),
    [positionInfos, activePositionId]
  )

  // Use ref to track if smooth scroll was already triggered for activePosition
  const hasScrolledToPosition = useRef(false)

  const [showNewPositionForm, setShowNewPositionForm] = useState(false)
  const [isDontShowSelected, setIsDontShowSelected] = useState(
    localStorage.getItem(DONT_SHOW_THIS_AGAIN_LP_LS_KEY) === 'true'
  )

  // Smooth scroll into new copied position
  useEffect(() => {
    const finishedInitialLoad = prevIsLoading === true && isLoading === false
    if (isCopiedPosition && !didScrollRef.current && finishedInitialLoad) {
      setActivePositionId(undefined)
      setTimeout(() => {
        smoothScrollToRef(newPositionRef, true, hasBanner)
        didScrollRef.current = true
      }, 200)
    }
  }, [isCopiedPosition, isLoading, prevIsLoading, hasBanner])

  useEffect(() => {
    if (!queryStringPositionId || clickedRef.current) {
      return
    }
    const newActive = positionInfos.find(p => p.positionId === queryStringPositionId)
    if (!newActive) {
      return
    }
    clickedRef.current = true
    setActivePositionId(newActive.positionId)
  }, [queryStringPositionId, positionInfos])

  useEffect(() => {
    setShowNewPositionForm(false)
    setActivePositionId(undefined)
  }, [pairInfo])

  // Smooth scroll into New position form
  useEffect(() => {
    smoothScrollToRef(newPositionRef, showNewPositionForm, hasBanner)
  }, [showNewPositionForm, isMobile, hasBanner])

  // Smooth scroll into position overview
  useEffect(() => {
    if (activePosition && !hasScrolledToPosition.current) {
      smoothScrollToRef(positionOverviewRef, !!activePosition, hasBanner)
      hasScrolledToPosition.current = true
    }
  }, [activePosition, isMobile, hasBanner])

  const {
    positionDetails,
    refetch: refetchPositionDetails,
    isLoading: isLoadingPositionDetails,
  } = useUserPositionDetails(activePosition?.positionId || '')
  const events = useMemo(() => positionDetails?.events || [], [positionDetails?.events])

  const setDontShowThisAgain = useCallback(() => {
    setIsDontShowSelected(true)
    localStorage.setItem(DONT_SHOW_THIS_AGAIN_LP_LS_KEY, 'true')
  }, [])

  const resetDontShowThisAgain = useCallback(() => {
    setIsDontShowSelected(false)
    localStorage.setItem(DONT_SHOW_THIS_AGAIN_LP_LS_KEY, 'false')
  }, [])

  const handleNewPositionClick = useCallback(() => {
    capture('lp:new_position_click', {
      entity_id: `${pairInfo.coinX.displaySymbol}-${pairInfo.coinY.displaySymbol}`,
    })
    setActivePositionId(undefined)
    setShowNewPositionForm(true)
    hasScrolledToPosition.current = false
  }, [capture, pairInfo.coinX.displaySymbol, pairInfo.coinY.displaySymbol])

  const handleNewPositionClose = useCallback(() => {
    setActivePositionId(undefined)
    setShowNewPositionForm(false)
    hasScrolledToPosition.current = false
    // Remove query params if we have copied position and user presses close button
    if (isCopiedPosition) {
      navigate(pathname)
    }
  }, [isCopiedPosition, navigate, pathname])

  const handleMyPositionsClick = useCallback(
    (positionId: string) => {
      if (isCopiedPosition) {
        navigate(pathname)
      }
      setActivePositionId(positionId)
      setShowNewPositionForm(false)
    },
    [isCopiedPosition, navigate, pathname]
  )

  const handlePositionCloseClick = useCallback(() => {
    navigate(window.location.pathname)
    setActivePositionId(undefined)
    setShowNewPositionForm(false)
    hasScrolledToPosition.current = false
  }, [navigate])

  const onCreatePositionSuccess = useCallback(() => {
    setActivePositionId(undefined)
    setShowNewPositionForm(false)
  }, [])

  const onCreatePositionProcessed = useCallback(
    async (newPositionId?: string) => {
      await refetchUserPositionInfos()
      if (!newPositionId) {
        return
      }
      void queryClient.invalidateQueries({
        queryKey: positionMetricsQueryKey(network, newPositionId),
      })
      void queryClient.invalidateQueries({
        queryKey: positionMetricsHistoryQueryKey(network, newPositionId),
      })
    },
    [refetchUserPositionInfos, queryClient, network]
  )

  const onReduceSuccess = useCallback(async () => {
    setActivePositionId(undefined)
  }, [])

  const onReduceProcessed = useCallback(async () => {
    await refetchUserPositionInfos()
  }, [refetchUserPositionInfos])

  const onDepositSuccess = useCallback(async () => {
    setActivePositionId(undefined)
  }, [])

  const onDepositProcessed = useCallback(async () => {
    await refetchUserPositionInfos()
  }, [refetchUserPositionInfos])

  useEffect(() => {
    setActivePositionId(undefined)
    setShowNewPositionForm(false)
  }, [walletAddress])

  const showEmptyPositions =
    positionInfos.length === 0 &&
    !showNewPositionForm &&
    typeof activePosition === 'undefined' &&
    !isLoading &&
    !isCopiedPosition

  const newPositionDisabled = pairInfo.configInfos.length === 0

  const tabData = activePosition
    ? [
        {
          label: 'Overview',
          content: (
            <>
              <PositionOverview
                positionInfo={activePosition}
                onCloseClick={handlePositionCloseClick}
                onReduceSuccess={onReduceSuccess}
                onReduceProcessed={onReduceProcessed}
                onDepositSuccess={onDepositSuccess}
                onDepositProcessed={onDepositProcessed}
                refetchPositionDetails={refetchPositionDetails}
                pairInfo={pairInfo}
              />
              <LpActivityHistory positionInfo={activePosition} events={events} isLoading={isLoadingPositionDetails} />
            </>
          ),
        },
        {
          label: 'Analytics',
          content: (
            <Analytics
              positionId={activePosition.positionId}
              isActivePosition={!activePosition.isDeleted && !activePosition.lastPosition.isInactive()}
              xSymbol={activePosition.lastPosition.X.symbol}
              ySymbol={activePosition.lastPosition.Y.symbol}
              configName={activePosition.lastPosition.configInfo.name}
            />
          ),
        },
      ]
    : null

  return (
    <Page $hasBanner={hasBanner}>
      <Breadcrumbs
        items={[
          { label: 'Home', path: '/' },
          { label: 'LP Vaults', path: '/lp-vaults' },
          {
            label: `${pairInfo.coinX.displaySymbol}-${pairInfo.coinY.displaySymbol}`,
            path: `/lp-vaults/${pairInfo.coinX.displaySymbol}-${pairInfo.coinY.displaySymbol}`,
          },
        ]}
      />
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <LpVaultsMenu
          displayInfo={pairInfo}
          isDontShowSelected={isDontShowSelected}
          onResetDontShowThisAgain={resetDontShowThisAgain}
          displayInfoList={PAIR_INFOS}
          handleNewPositionClick={handleNewPositionClick}
          newPositionDisabled={newPositionDisabled}
        />
        {!isDontShowSelected && (
          <NewPositionButton
            onClick={handleNewPositionClick}
            style={{ marginLeft: 'auto' }}
            disabled={!isConnected || newPositionDisabled}
          >
            New Position <IconPlus />
          </NewPositionButton>
        )}
      </div>
      <div style={{ marginLeft: 'auto' }}>
        <LpVaultExplanation isDontShowSelected={isDontShowSelected} onDontShowThisAgainClick={setDontShowThisAgain} />
      </div>
      {isLoading ? (
        <MyPositionsContentLoader />
      ) : (
        <MyPositions
          activePositionId={activePosition?.positionId}
          onClick={handleMyPositionsClick}
          positionInfos={positionInfos}
          queryStringPositionId={queryStringPositionId}
          displayPairId={`${pairInfo.coinX.typeName}-${pairInfo.coinY.typeName}`}
        />
      )}
      {showEmptyPositions && (
        <NoAvailablePosition onNewPositionClick={handleNewPositionClick} newPositionDisabled={newPositionDisabled} />
      )}
      {((showNewPositionForm && typeof activePosition === 'undefined') || isCopiedPosition) && (
        <NewPosition
          pairInfo={pairInfo}
          onClose={handleNewPositionClose}
          onCreatePositionSuccess={onCreatePositionSuccess}
          onCreatePositionProcessed={onCreatePositionProcessed}
          ref={newPositionRef}
          initialConfigId={isCopiedPosition ? initialConfigId : undefined}
          initialMinPrice={isCopiedPosition ? initialMinPrice : undefined}
          initialMaxPrice={isCopiedPosition ? initialMaxPrice : undefined}
        />
      )}
      {activePosition && tabData && (
        <Tabs
          tabs={tabData}
          tabStyles={{ maxWidth: 180 }}
          tabsStyles={{ justifyContent: 'flex-start', marginTop: 24 }}
          tabPanelStyles={{ padding: '0', fontSize: 20 }}
          ref={positionOverviewRef}
        />
      )}
    </Page>
  )
}
